'use client';

import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import Image from 'next/image';
import type { CourseImages } from '@udemy-v2/browse-course';
import { Button } from '@udemy-v2/react-core-components';
import { PlayOverlay } from '@udemy-v2/react-structure-components';
import React, { useState, useMemo, useCallback } from 'react';
import { useI18n } from '@udemy-v2/i18n';
import { ColorScheme } from '@udemy-v2/styles';
import styles from './course-preview-entry-point.module.scss';
import { useClpCourse } from '../../context/clp-course-provider.react-component';
import { CoursePreviewModal } from '../course-preview/course-preview-modal.react-component';
import { usePositionMarker } from '@/components/position-marker';
import { useAssetImage } from '@/features/course-landing-page/context/asset-image-provider.react-component';
import { usePreviewableLectures } from "@/features/course-landing-page/components/course-preview/context";

// Base 64 encoded 500x500 transparent placeholder image used for LCP optimization
const placeholderBase64500x500 = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAfQAAAH0CAIAAABEtEjdAAABgmlDQ1BzUkdCIElFQzYxOTY2LTIuMQAAKJF1kc8rRFEUxz8zgxGjERaUxaRhhcYosVFGQk2axii/NjPP/FDzxuu9mSRbZTtFiY1fC/4CtspaKSIlO2VNbNBz3oyaSebczj2f+733nO49F+yRtKIaVT5QM1k9PB7wzM7Ne5zP1NBGE63YooqhjYRCQSraxx02K970WLUqn/vX6pfihgK2WuFhRdOzwhPCwdWsZvG2cIuSii4Jnwp363JB4VtLjxX5xeJkkb8s1iPhUbA3CnuSZRwrYyWlq8LycrxqOqf83sd6iSuemZmW2CHejkGYcQJ4mGSMUQboY0jmAXrw0ysrKuT7CvlTrEiuIrPGGjrLJEmRpVvUnFSPS0yIHpeRZs3q/9++Gol+f7G6KwDVT6b51gnOLfjOm+bnoWl+H4HjES4ypfyVAxh8Fz1f0rz74N6As8uSFtuB801ofdCierQgOcTtiQS8nkDDHDRfQ91CsWe/+xzfQ2RdvuoKdvegS867F38AGNNnwvJxPYUAAAAJcEhZcwAACxMAAAsTAQCanBgAAALtSURBVHic7cEBAQAAAIIg/69uSEABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA/Bh0SQABe+rNwwAAAABJRU5ErkJggg==';
interface AssetImageProps {
  images: CourseImages;
  sizes?: string;
}
export const AssetImage = t0 => {
  const $ = _c(4);
  const {
    images,
    sizes: t1
  } = t0;
  const sizes = t1 === undefined ? "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw" : t1;
  const primarySrc = images.px480x270 || images.px304x171 || images.px240x135;
  if (!primarySrc) {
    return null;
  }
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      objectFit: "cover"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== primarySrc || $[2] !== sizes) {
    t3 = <Image src={primarySrc} alt="" fill={true} style={t2} sizes={sizes} priority={true} placeholder="blur" blurDataURL={placeholderBase64500x500} />;
    $[1] = primarySrc;
    $[2] = sizes;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};
interface CoursePreviewEntryPointProps {
  // TODO: Determine what image sizes to use for the image
  imageSizes?: string;
  lectureId?: string;
  // UI region for event tracking - will be passed to event tracking in CoursePreviewModal
  uiRegion: string;
  // Whether to hide/fade this component when CourseLede goes out of view
  hideWhenLedeOutOfView?: boolean;
}

/**
 * A wrapper component that handles responsive rendering of CoursePreviewEntryPoint
 * to prevent layout shift and unwanted flashing on desktop.
 *
 * This component always renders but uses CSS media queries to control visibility,
 * ensuring consistent layout behavior across device types.
 */
export const CoursePreviewEntryPointWrapper = t0 => {
  const $ = _c(7);
  const {
    uiRegion,
    hideWhenLedeOutOfView: t1,
    lectureId
  } = t0;
  const hideWhenLedeOutOfView = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== hideWhenLedeOutOfView || $[1] !== lectureId || $[2] !== uiRegion) {
    t2 = <div className={styles["mobile-preview"]}><CoursePreviewEntryPoint uiRegion={uiRegion} hideWhenLedeOutOfView={hideWhenLedeOutOfView} lectureId={lectureId} /></div>;
    $[0] = hideWhenLedeOutOfView;
    $[1] = lectureId;
    $[2] = uiRegion;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className={styles["desktop-placeholder"]} aria-hidden="true" />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2) {
    t4 = <div className={styles.placeholder}>{t2}{t3}</div>;
    $[5] = t2;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
};
const CoursePreviewEntryPointInternal = t0 => {
  const $ = _c(31);
  const {
    lectureId,
    uiRegion,
    hideWhenLedeOutOfView: t1
  } = t0;
  const hideWhenLedeOutOfView = t1 === undefined ? true : t1;
  const {
    gettext
  } = useI18n();
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const {
    isComponentInView
  } = usePositionMarker();
  const {
    preloadedAssetImage
  } = useAssetImage();
  const course = useClpCourse();
  const previewableLectures = usePreviewableLectures();
  const {
    promoVideoAsset
  } = course;
  const showPreviewEntryPoint = !!promoVideoAsset || previewableLectures.length > 0;
  const isVisible = hideWhenLedeOutOfView ? isComponentInView : true;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setIsPreviewModalOpen(true);
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const showPreviewModal = t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setIsPreviewModalOpen(false);
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const hidePreviewModal = t3;
  let t4;
  if ($[2] !== gettext || $[3] !== preloadedAssetImage || $[4] !== showPreviewEntryPoint) {
    t4 = showPreviewEntryPoint && <Button className={classNames("ud-custom-focus-visible", "ud-link-knockout", styles["placeholder-btn"])} onClick={showPreviewModal} udStyle="ghost" aria-label={gettext("Play course preview")}><span className={styles["img-aspect"]}>{preloadedAssetImage}</span><span className={classNames(styles.overlay, styles.gradient)} /><PlayOverlay size="xxlarge" /><span className={classNames("ud-heading-md", styles.overlay, styles.text)}>{gettext("Preview this course")}</span></Button>;
    $[2] = gettext;
    $[3] = preloadedAssetImage;
    $[4] = showPreviewEntryPoint;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== preloadedAssetImage || $[7] !== showPreviewEntryPoint) {
    t5 = !showPreviewEntryPoint && <div className={styles["placeholder-img"]}><div className={styles["img-aspect"]}>{preloadedAssetImage}</div></div>;
    $[6] = preloadedAssetImage;
    $[7] = showPreviewEntryPoint;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = <div className={styles.asset}>{t4}{t5}</div>;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== isPreviewModalOpen || $[13] !== lectureId || $[14] !== uiRegion) {
    t7 = <CoursePreviewModal onClose={hidePreviewModal} isOpen={isPreviewModalOpen} uiRegion={uiRegion} selectedLectureId={lectureId} />;
    $[12] = isPreviewModalOpen;
    $[13] = lectureId;
    $[14] = uiRegion;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t6 || $[17] !== t7) {
    t8 = <>{t6}{t7}</>;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const previewEntryPoint = t8;
  if (!hideWhenLedeOutOfView) {
    let t9;
    if ($[19] !== previewEntryPoint) {
      t9 = <ColorScheme colorScheme="dark" className={styles.wrapper}>{previewEntryPoint}</ColorScheme>;
      $[19] = previewEntryPoint;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    return t9;
  }
  const t9 = !isVisible;
  let t10;
  if ($[21] !== t9) {
    t10 = classNames(styles["transition-container"], {
      [styles.collapsed]: t9
    });
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const t11 = !isVisible;
  let t12;
  if ($[23] !== t11) {
    t12 = classNames(styles.wrapper, {
      [styles["fade-out"]]: t11
    });
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== previewEntryPoint || $[26] !== t12) {
    t13 = <ColorScheme colorScheme="dark" className={t12}>{previewEntryPoint}</ColorScheme>;
    $[25] = previewEntryPoint;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t10 || $[29] !== t13) {
    t14 = <div className={t10}>{t13}</div>;
    $[28] = t10;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  return t14;
};

// Memoize the component to prevent re-renders when props don't change
// This is critical for LCP performance when the component is used in the sidebar
export const CoursePreviewEntryPoint = React.memo(CoursePreviewEntryPointInternal);